// 로그인 토큰(JWT) 저장소 — 예전에는 localStorage('token')에 두었고, 지금은 쿠키에 둔다
// (recorder_manager_view의 components/utils/tokenStore.js와 같은 방식).
//   - 쿠키 이름·허용 도메인은 시스템(브랜드)·스테이지마다 brands.json 으로 정한다(config/brands.ts).
//     관리 화면과 이름이 다르다 — 같으면 서로의 토큰을 덮어써 상대 앱에서 로그아웃된다.
//   - 화면이 토큰을 읽어 Authorization 헤더에 붙이므로 HttpOnly 가 아니다(보안 수준은 localStorage와 같다).
//     서버는 쿠키 로그인(/auth/cookie-login)에서만 이 쿠키를 읽어 서명·만료를 검증한다.
//   - 만료는 JWT exp 에 맞춘다(쿠키가 토큰보다 오래 남지 않게).
//   - 현재 주소가 설정 도메인에 속하지 않으면(로컬 localhost 등) 브라우저가 쿠키를 조용히 버려 로그인이 안 되므로,
//     그때는 도메인 없이 현재 호스트 전용으로 저장한다.
//   - 예전 localStorage 에 남은 토큰은 처음 읽을 때 쿠키로 옮기고 지운다(이미 로그인한 기록자가 튕기지 않게).
// 쿠키 문자열을 만들고 읽는 부분은 순수 함수로 두었다 — vitest가 node 환경이라 document.cookie 가 없다.

import { getTokenCookieDomain, getTokenCookieName } from '@/config/brands'
import { decodeJwtExpMs } from './jwt'

const LEGACY_STORAGE_KEY = 'token'

/** 쿠키 문자열에서 값을 찾아 URL 디코드해 돌려준다. 없거나 디코드할 수 없으면 undefined. */
export function readCookie(name: string, cookieString: string): string | undefined {
  for (const part of cookieString.split(';')) {
    const eq = part.indexOf('=')
    if (eq < 0) continue
    if (part.slice(0, eq).trim() !== name) continue
    const raw = part.slice(eq + 1).trim()
    if (!raw) return undefined
    try {
      return decodeURIComponent(raw)
    } catch {
      return undefined
    }
  }
  return undefined
}

/**
 * 설정된 허용 도메인을 현재 호스트에서 쓸 수 있으면 그 도메인(앞에 점), 아니면 ''(현재 호스트 전용).
 * 예) ".kbostats.net": recorderapp.kbostats.net / kbostats.net → 사용, localhost / recorderapp.kbostats.com → 사용 안 함
 */
export function cookieDomainForHost(domain: string, hostname: string): string {
  const base = domain.trim().replace(/^\.+/, '').toLowerCase()
  const host = hostname.toLowerCase()
  if (!base || !host) return ''
  return host === base || host.endsWith(`.${base}`) ? `.${base}` : ''
}

/** 토큰 만료까지 남은 초. 토큰 형식을 못 읽으면 undefined(세션 쿠키). */
export function secondsUntilExpiry(token: string, nowMs: number): number | undefined {
  const expMs = decodeJwtExpMs(token)
  if (expMs === undefined) return undefined
  return Math.max(0, Math.floor((expMs - nowMs) / 1000))
}

interface CookieOptions {
  domain: string
  secure: boolean
  maxAgeSeconds?: number
}

/** document.cookie 에 넣을 문자열 */
export function buildCookie(name: string, value: string, opts: CookieOptions): string {
  const attrs = ['Path=/', 'SameSite=Lax']
  if (opts.secure) attrs.push('Secure')
  if (opts.domain) attrs.push(`Domain=${opts.domain}`)
  if (opts.maxAgeSeconds !== undefined) attrs.push(`Max-Age=${opts.maxAgeSeconds}`)
  return `${name}=${encodeURIComponent(value)}; ${attrs.join('; ')}`
}

function currentCookieOptions(maxAgeSeconds?: number): CookieOptions {
  return {
    domain: cookieDomainForHost(getTokenCookieDomain(), window.location.hostname),
    secure: window.location.protocol === 'https:',
    maxAgeSeconds,
  }
}

/** 저장된 토큰. 없으면 null (예전 localStorage.getItem('token') 자리) */
export function getStoredToken(): string | null {
  const value = readCookie(getTokenCookieName(), document.cookie)
  if (value) return value
  try {
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY)
    if (legacy) {
      setStoredToken(legacy)
      localStorage.removeItem(LEGACY_STORAGE_KEY)
      return legacy
    }
  } catch {
    // localStorage 를 쓸 수 없는 환경
  }
  return null
}

/** 토큰 저장 (예전 localStorage.setItem('token', token) 자리) */
export function setStoredToken(token: string): void {
  if (!token) {
    removeStoredToken()
    return
  }
  document.cookie = buildCookie(getTokenCookieName(), token, currentCookieOptions(secondsUntilExpiry(token, Date.now())))
}

/** 토큰 삭제 (예전 localStorage.removeItem('token') 자리). 남아 있을 수 있는 localStorage 값도 함께 지운다. */
export function removeStoredToken(): void {
  document.cookie = buildCookie(getTokenCookieName(), '', currentCookieOptions(0))
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY)
  } catch {
    // localStorage 를 쓸 수 없는 환경
  }
}
